import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  Tabs,
  Tab,
  Chip,
  FormControlLabel,
  Switch,
  CardContent
} from '@mui/material';
import { AccountCircle, Lock, Group, Person, Security } from '@mui/icons-material';
import { useComponentWebSocket } from './GlobalSettingUtils';
import AccountManagement from '../AccountManagement';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';

// TabPanel helper with consistent styling
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div 
      role="tabpanel" 
      hidden={value !== index} 
      {...other}
      style={{
        height: value === index ? 'auto' : 0,
        overflow: value === index ? 'visible' : 'hidden',
      }}
    >
      {value === index && (
        <Box sx={{ 
          pt: { xs: 2, sm: 3 },
          px: { xs: 1, sm: 2 },
          minHeight: { xs: 250, sm: 300 }
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AccountSettings = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaProvisioningUri, setMfaProvisioningUri] = useState('');
  const [mfaQrData, setMfaQrData] = useState('');
  const [mfaSetupCode, setMfaSetupCode] = useState('');
  const [mfaSetupError, setMfaSetupError] = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [mfaDisablePassword, setMfaDisablePassword] = useState('');
  const [mfaDisableError, setMfaDisableError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [globalMfaRequired, setGlobalMfaRequired] = useState(false);
  const [globalMfaLoading, setGlobalMfaLoading] = useState(false);
  const [globalMfaError, setGlobalMfaError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [userInfo, setUserInfo] = useState({ username: '', permissions: '' });
  const globalMfaPrevRef = useRef(false);

  // Define consistent colors and styling
  const primaryColor = '#5757b0';
  const lightPurple = '#f0f0ff';
  const hoverColor = '#aaaaca';

  // Define command handlers for WebSocket messages
  const commandHandlers = {
    'change_password': (message) => {
      setLoading(false);
      if (message.data.status === 'success') {
        setMessage({ type: 'success', text: message.data.message });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: message.data.message });
      }
      setShowSnackbar(true);
    },
    'login_response': (message) => {
      console.log('Login response received:', message);
      if (message.data && message.data.success) {
        setUserInfo({
          username: message.data.username || '',
          permissions: message.data.permissions || ''
        });
      }
    },
    'session_validated': (message) => {
      console.log('Session validated message received:', message);
      if (message.data && message.data.success) {
        setUserInfo({
          username: message.data.username || '',
          permissions: message.data.permissions || ''
        });
      }
    },
    'validate_session': (message) => {
      console.log('Validate session message received:', message);
      // Handle both old format (valid) and new format (success)
      const isValid = message.data && (message.data.success || message.data.valid);
      if (isValid) {
        setUserInfo({
          username: message.data.username || '',
          permissions: message.data.permissions || ''
        });
      }
    },
    'get_mfa_status': (message) => {
      if (message.data?.success) {
        const enabled = !!message.data.enabled;
        const pending = !!message.data.pending;
        setMfaEnabled(enabled);
        setMfaPending(pending);
        if (!pending) {
          setMfaSecret('');
          setMfaSetupCode('');
          setMfaProvisioningUri('');
          setMfaQrData('');
        }
      } else if (message.data?.message) {
        setMessage({ type: 'error', text: message.data.message });
        setShowSnackbar(true);
      }
    },
    'get_global_mfa_requirement': (message) => {
      if (message.data?.success) {
        setGlobalMfaRequired(!!message.data.required);
        setGlobalMfaError('');
      } else {
        const errorMessage = message.data?.message || t('account_settings.global_mfa_load_failed');
        setGlobalMfaError(errorMessage);
        setMessage({ type: 'error', text: errorMessage });
        setShowSnackbar(true);
      }
    },
    'set_global_mfa_requirement': (message) => {
      setGlobalMfaLoading(false);
      if (message.data?.success) {
        setGlobalMfaRequired(!!message.data.required);
        setGlobalMfaError('');
        setMessage({ type: 'success', text: message.data?.message || t('account_settings.global_mfa_updated') });
      } else {
        const errorMessage = message.data?.message || t('account_settings.global_mfa_update_failed');
        setGlobalMfaRequired(globalMfaPrevRef.current);
        setGlobalMfaError(errorMessage);
        setMessage({ type: 'error', text: errorMessage });
      }
      setShowSnackbar(true);
    },
    'begin_mfa_setup': (message) => {
      setMfaLoading(false);
      if (message.data?.success) {
        setMfaPending(true);
        setMfaSecret(message.data.secret || '');
        setMfaProvisioningUri(message.data.provisioning_uri || '');
        setMfaQrData('');
        setMfaSetupCode('');
        setMfaSetupError('');
        setMessage({ type: 'success', text: t('account_settings.mfa_setup_started') });
      } else {
        setMessage({ type: 'error', text: message.data?.message || t('account_settings.mfa_setup_failed') });
      }
      setShowSnackbar(true);
    },
    'verify_mfa_setup': (message) => {
      setMfaLoading(false);
      if (message.data?.success) {
        setMfaEnabled(true);
        setMfaPending(false);
        setMfaSecret('');
        setMfaProvisioningUri('');
        setMfaQrData('');
        setMfaSetupCode('');
        setMfaSetupError('');
        setMessage({ type: 'success', text: message.data?.message || t('account_settings.mfa_enabled_success') });
      } else {
        setMfaSetupError(message.data?.message || t('account_settings.mfa_invalid_code'));
        setMessage({ type: 'error', text: message.data?.message || t('account_settings.mfa_verify_failed') });
      }
      setShowSnackbar(true);
    },
    'disable_mfa': (message) => {
      setMfaLoading(false);
      if (message.data?.success) {
        setMfaEnabled(false);
        setMfaPending(false);
        setMfaSecret('');
        setMfaProvisioningUri('');
        setMfaQrData('');
        setMfaDisableCode('');
        setMfaDisablePassword('');
        setMfaDisableError('');
        setMessage({ type: 'success', text: message.data?.message || t('account_settings.mfa_disabled_success') });
      } else {
        setMfaDisableError(message.data?.message || t('account_settings.mfa_invalid_code'));
        setMessage({ type: 'error', text: message.data?.message || t('account_settings.mfa_disable_failed') });
      }
      setShowSnackbar(true);
    }
  };

  console.log('AccountSettings rendered with userInfo:', userInfo);
  
  const { sendCommand } = useComponentWebSocket(commandHandlers);

  useEffect(() => {
    if (userInfo.username) {
      sendCommand('get_mfa_status', {});
    }
  }, [userInfo.username, sendCommand]);

  useEffect(() => {
    if (userInfo.permissions === 'admin') {
      sendCommand('get_global_mfa_requirement', {});
    }
  }, [userInfo.permissions, sendCommand]);

  useEffect(() => {
    let isActive = true;
    if (!mfaProvisioningUri) {
      setMfaQrData('');
      return () => {};
    }

    setMfaQrData('');
    QRCode.toDataURL(mfaProvisioningUri, { margin: 1, width: 200 })
      .then((url) => {
        if (isActive) setMfaQrData(url);
      })
      .catch((err) => {
        console.error('Failed to generate MFA QR code', err);
        if (isActive) setMfaQrData('');
      });

    return () => {
      isActive = false;
    };
  }, [mfaProvisioningUri]);

  // Request current session validation when component mounts
  useEffect(() => {
    console.log('AccountSettings mounted...');
    // Check if user is logged in and has session token
    const isLoggedIn = localStorage.getItem('dataunfold_logged_in') === 'true';
    const sessionToken = localStorage.getItem('dataunfold_session_token');
    
    if (isLoggedIn && sessionToken) {
      console.log('User is logged in, requesting session validation for user info...');
      // Request session validation to get user info
      sendCommand('validate_session', { session_token: sessionToken });
    } else {
      console.log('User not logged in or no session token found');
    }
  }, [sendCommand]);  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    // You can add any methods here if needed for parent component interaction
  }), []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePasswordChange = async () => {
    // Validate form
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: t('account_settings.all_fields_required') });
      setShowSnackbar(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: t('account_settings.passwords_not_match') });
      setShowSnackbar(true);
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: t('account_settings.password_too_short') });
      setShowSnackbar(true);
      return;
    }

    if (newPassword === currentPassword) {
      setMessage({ type: 'error', text: t('account_settings.password_must_differ') });
      setShowSnackbar(true);
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    // Send password change request
    sendCommand('change_password', {
      currentPassword,
      newPassword
    });
  };

  const handleBeginMfaSetup = () => {
    setMfaLoading(true);
    setMessage({ type: '', text: '' });
    sendCommand('begin_mfa_setup', {});
  };

  const handleVerifyMfaSetup = () => {
    if (!mfaSetupCode.trim()) {
      setMessage({ type: 'error', text: t('account_settings.mfa_code_required') });
      setShowSnackbar(true);
      return;
    }
    setMfaSetupError('');
    setMfaLoading(true);
    sendCommand('verify_mfa_setup', { code: mfaSetupCode.trim() });
  };

  const handleDisableMfa = () => {
    if (!mfaDisablePassword.trim() || !mfaDisableCode.trim()) {
      setMessage({ type: 'error', text: t('account_settings.mfa_password_code_required') });
      setShowSnackbar(true);
      return;
    }
    setMfaDisableError('');
    setMfaLoading(true);
    sendCommand('disable_mfa', {
      password: mfaDisablePassword,
      code: mfaDisableCode.trim()
    });
  };

  const handleGlobalMfaToggle = (event) => {
    const nextValue = event.target.checked;
    globalMfaPrevRef.current = globalMfaRequired;
    setGlobalMfaRequired(nextValue);
    setGlobalMfaLoading(true);
    setGlobalMfaError('');
    sendCommand('set_global_mfa_requirement', { required: nextValue });
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const mfaStatusLabel = mfaEnabled ? t('account_settings.mfa_status_enabled') : (mfaPending ? t('account_settings.mfa_status_pending') : t('account_settings.mfa_status_disabled'));
  const mfaStatusColor = mfaEnabled ? 'success' : (mfaPending ? 'warning' : 'default');

  return (
    <Box sx={{ 
      maxWidth: '100%', 
      mx: 'auto',
      background: 'transparent'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: { xs: 3, sm: 4 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1.5,
            mb: 2,
            color: primaryColor,
            fontWeight: 700,
            fontSize: { 
              xs: '1.5rem', 
              sm: '1.75rem', 
              md: '2rem'
            },
            letterSpacing: '0.02em'
          }}
        >
          <AccountCircle sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />
          {t('account_settings.title')}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#666',
            fontSize: '0.95rem',
            maxWidth: 500,
            mx: 'auto'
          }}
        >
          {t('account_settings.subtitle')}
        </Typography>
      </Box>

      {/* User Information Display */}
      {userInfo.username && (
        <Paper 
          elevation={6} 
          sx={{ 
            mb: 4, 
            background: `linear-gradient(135deg, ${lightPurple} 0%, #fafafa 100%)`,
            border: `1px solid ${primaryColor}20`,
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, ${primaryColor} 0%, #7575c7 50%, ${primaryColor} 100%)`,
            }
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 }, pt: { xs: 3.5, sm: 4.5 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${hoverColor} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 12px ${primaryColor}30`
                    }}
                  >
                    <Person sx={{ color: 'white', fontSize: '1.8rem' }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                      {t('account_settings.logged_in_as')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', mt: 0.5 }}>
                      {userInfo.username}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  gap: 2
                }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: userInfo.permissions === 'admin' ? 
                        `linear-gradient(135deg, #4CAF50 0%, #45a049 100%)` :
                        `linear-gradient(135deg, #757575 0%, #616161 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 3px 10px rgba(0,0,0,0.2)'
                    }}
                  >
                    <Security sx={{ color: 'white', fontSize: '1.5rem' }} />
                  </Box>
                  <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                      {t('account_settings.permission_level')}
                    </Typography>
                    <Chip 
                      label={userInfo.permissions || 'user'}
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        height: 32,
                        mt: 0.5,
                        background: userInfo.permissions === 'admin' ? 
                          `linear-gradient(135deg, ${primaryColor} 0%, ${hoverColor} 100%)` : 
                          'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                        color: userInfo.permissions === 'admin' ? 'white' : '#666',
                        boxShadow: userInfo.permissions === 'admin' ? 
                          `0 2px 8px ${primaryColor}40` : 
                          '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Paper>
      )}

      <Paper
        elevation={12}
        sx={{
          background: 'white',
          border: `2px solid ${primaryColor}`,
          borderRadius: 4,
          boxShadow: `0px 16px 48px rgba(87, 87, 176, 0.3), 0px 8px 24px rgba(87, 87, 176, 0.2)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${primaryColor} 0%, #7575c7 50%, ${primaryColor} 100%)`,
          }
        }}
      >
        {/* Enhanced Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          background: lightPurple,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 }
        }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 48, sm: 56 },
              '& .MuiTabs-indicator': {
                backgroundColor: primaryColor,
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTabs-scrollButtons': {
                color: primaryColor,
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
              '& .MuiTab-root': {
                color: '#666',
                minHeight: { xs: 48, sm: 56 },
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.5 },
                minWidth: { xs: 120, sm: 140 },
                transition: 'all 0.3s ease-in-out',
                '&.Mui-selected': {
                  color: primaryColor,
                  fontWeight: 700,
                  backgroundColor: 'rgba(87, 87, 176, 0.08)',
                  transform: { xs: 'none', sm: 'translateY(-1px)' }
                },
                '&:hover': {
                  color: hoverColor,
                  backgroundColor: 'rgba(87, 87, 176, 0.05)',
                  transform: { xs: 'none', sm: 'translateY(-1px)' },
                }
              }
            }}
          >
            <Tab 
              icon={<Lock />} 
              label={t('account_settings.tab_password')} 
              iconPosition="start"
              sx={{ 
                gap: 1,
                '& .MuiSvgIcon-root': { 
                  fontSize: '1.2rem' 
                }
              }}
            />
            {userInfo.permissions === 'admin' && (
              <Tab 
                icon={<Group />} 
                label={t('account_settings.tab_users_management')} 
                iconPosition="start"
                sx={{ 
                  gap: 1,
                  '& .MuiSvgIcon-root': { 
                    fontSize: '1.2rem' 
                  }
                }}
              />
            )}
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                mb: 4,
                color: '#333',
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              <Lock sx={{ color: primaryColor }} />
              {t('account_settings.change_password_title')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('account_settings.current_password')}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  variant="outlined"
                  disabled={loading}
                  placeholder={t('account_settings.current_password_placeholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                        borderWidth: 2
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                      fontWeight: 600
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('account_settings.new_password')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  variant="outlined"
                  disabled={loading}
                  placeholder={t('account_settings.new_password_placeholder')}
                  helperText={t('account_settings.new_password_helper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                        borderWidth: 2
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                      fontWeight: 600
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#666',
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('account_settings.confirm_new_password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="outlined"
                  disabled={loading}
                  placeholder={t('account_settings.confirm_new_password_placeholder')}
                  helperText={t('account_settings.confirm_new_password_helper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '&:hover fieldset': {
                        borderColor: primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                        borderWidth: 2
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: primaryColor,
                      fontWeight: 600
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#666',
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 3, backgroundColor: '#e0e0e0' }} />
                
                {/* Password Requirements */}
                <Paper sx={{ 
                  p: 3, 
                  backgroundColor: lightPurple, 
                  borderRadius: 2,
                  border: `1px solid ${primaryColor}20`,
                  mb: 3
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>
                    {t('account_settings.password_requirements_title')}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1, display: 'flex', alignItems: 'center' }}>
                      {t('account_settings.password_requirement_min')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', display: 'flex', alignItems: 'center' }}>
                      {t('account_settings.password_requirement_different')}
                    </Typography>
                  </Box>
                </Paper>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handlePasswordChange}
                    disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                    sx={{
                      backgroundColor: primaryColor,
                      borderRadius: 3,
                      px: { xs: 4, sm: 6 },
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: `0px 8px 24px rgba(87, 87, 176, 0.3)`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#4a4a9d',
                        transform: 'translateY(-2px)',
                        boxShadow: `0px 12px 32px rgba(87, 87, 176, 0.4)`,
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc',
                        boxShadow: 'none',
                        transform: 'none'
                      },
                    }}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Lock />}
                  >
                    {loading ? t('account_settings.changing_password') : t('account_settings.change_password_button')}
                  </Button>
                </Box>

                <Divider sx={{ my: 4, backgroundColor: '#e0e0e0' }} />

                <Paper sx={{ 
                  p: 3, 
                  backgroundColor: '#f9f9ff', 
                  borderRadius: 2,
                  border: `1px solid ${primaryColor}20`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Security sx={{ color: primaryColor }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                        {t('account_settings.mfa_title')}
                      </Typography>
                    </Box>
                    <Chip
                      label={mfaStatusLabel}
                      color={mfaStatusColor}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    {t('account_settings.mfa_subtitle')}
                  </Typography>

                  {!mfaEnabled ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {mfaPending && !mfaSecret && (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          {t('account_settings.mfa_setup_pending_alert')}
                        </Alert>
                      )}
                      <Button
                        variant="outlined"
                        onClick={handleBeginMfaSetup}
                        disabled={mfaLoading}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                      >
                        {mfaLoading ? t('account_settings.mfa_generating') : t('account_settings.mfa_generate_secret')}
                      </Button>

                      {mfaSecret && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {mfaProvisioningUri && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                              {mfaQrData ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    component="img"
                                    src={mfaQrData}
                                    alt="MFA QR code"
                                    sx={{ width: 180, height: 180, borderRadius: 2, border: '1px solid #e0e0e0' }}
                                  />
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    {t('account_settings.mfa_scan_qr')}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={20} />
                                  <Typography variant="body2" sx={{ color: '#666' }}>
                                    {t('account_settings.mfa_generating_qr')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                          <TextField
                            fullWidth
                            label={t('account_settings.mfa_authenticator_secret')}
                            value={mfaSecret}
                            InputProps={{ readOnly: true }}
                            helperText={t('account_settings.mfa_secret_helper')}
                          />
                          <TextField
                            fullWidth
                            label={t('account_settings.mfa_verification_code')}
                            value={mfaSetupCode}
                            onChange={(e) => {
                              setMfaSetupCode(e.target.value);
                              if (mfaSetupError) setMfaSetupError('');
                            }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            error={!!mfaSetupError}
                            helperText={mfaSetupError || ' '}
                          />
                          <Button
                            variant="contained"
                            onClick={handleVerifyMfaSetup}
                            disabled={mfaLoading || !mfaSetupCode.trim()}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                          >
                            {mfaLoading ? t('account_settings.mfa_verifying') : t('account_settings.mfa_verify_enable')}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        type="password"
                        label={t('account_settings.current_password')}
                        value={mfaDisablePassword}
                        onChange={(e) => setMfaDisablePassword(e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label={t('account_settings.mfa_authenticator_code')}
                        value={mfaDisableCode}
                        onChange={(e) => {
                          setMfaDisableCode(e.target.value);
                          if (mfaDisableError) setMfaDisableError('');
                        }}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        error={!!mfaDisableError}
                        helperText={mfaDisableError || ' '}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDisableMfa}
                        disabled={mfaLoading || !mfaDisablePassword.trim() || !mfaDisableCode.trim()}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                      >
                        {mfaLoading ? t('account_settings.mfa_disabling') : t('account_settings.mfa_disable')}
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {userInfo.permissions === 'admin' && (
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: { xs: 3, sm: 4 } }}>
              <Paper
                sx={{
                  p: 3,
                  backgroundColor: '#fffaf2',
                  borderRadius: 2,
                  border: `1px solid ${primaryColor}20`,
                  mb: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Security sx={{ color: primaryColor }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                    {t('account_settings.organization_mfa_title')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  {t('account_settings.organization_mfa_subtitle')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={globalMfaRequired}
                      onChange={handleGlobalMfaToggle}
                      disabled={globalMfaLoading}
                      color="primary"
                    />
                  }
                  label={globalMfaRequired ? t('account_settings.organization_mfa_required') : t('account_settings.organization_mfa_optional')}
                />
                {globalMfaLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {t('account_settings.organization_mfa_updating')}
                    </Typography>
                  </Box>
                )}
                {globalMfaError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {globalMfaError}
                  </Alert>
                )}
              </Paper>
              <AccountManagement
                userPermission={userInfo.permissions}
                currentUsername={userInfo.username}
              />
            </Box>
          </TabPanel>
        )}
      </Paper>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={message.type} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default AccountSettings;
